import { CloseIcon } from '@chakra-ui/icons';
import {
  AspectRatio,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useToast
} from '@chakra-ui/react';
import SearchAdaccountAdspresso from 'components/bmControl/SearchAdaccountAdspresso';
import Pagination from 'components/drive/PaginationForDrive';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useGetBmAdAccountsQuery } from 'store/BmAccounts/bmAccountApi';
import { setAfter, setBefore } from 'store/campaigns/campaignsSlice';
import { useUploadVideoMutation } from 'store/driveUpload/uploadApi';
import {
  setFiles,
  setIsWidgetOpen,
  setUploadAbortControllers,
  updateUploadStatus
} from 'store/driveUpload/uploadSlice';
import { adUplaodSchema } from 'utils/schema';

import { useRenderedVideosQuery } from 'store/aiHub/aiGenerationApi';

export default function UploadRenderVideosModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const toast = useToast();

  const [afterValue, setAftervalue] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRenderVideos, setSelectedRenderVideos] = useState([]);
  const [selectedAdAccounts, setSelectedAdAccounts] = useState([]);

  // Pagination (modal-local)
  const [currentPage, setCurrentPage] = useState(1);

  // Keep previous page items to avoid flicker while fetching next page
  const [stableVideos, setStableVideos] = useState([]);
  const rightPaneRef = useRef(null);

  useEffect(() => {
    if (isOpen) setCurrentPage(1);
  }, [isOpen]);

  const {
    data: renderedVideosResp,
    isLoading: isRendLoading,
    isFetching: isRendFetching,
  } = useRenderedVideosQuery({ currentPage }, { skip: !isOpen });

  const renderedVideos = renderedVideosResp?.data ?? [];
  const meta = renderedVideosResp?.meta ?? {};
  const pageSize = Number(meta?.pageSize || 10);
  const totalItems = Number(meta?.totalItems || 0);

  // Update stable list only when we actually have new data
  useEffect(() => {
    if (renderedVideos.length) setStableVideos(renderedVideos);
    // if response is empty but fetching: keep old stableVideos
  }, [renderedVideos]);

  const pageMetaForComponent = useMemo(
    () => ({
      after: undefined,
      before: undefined,
      pageSize,
      hasNextPage: Boolean(meta?.hasNextPage),
      hasPrevPage: Boolean(meta?.hasPrevPage),
      totalItems
    }),
    [meta?.hasNextPage, meta?.hasPrevPage, pageSize, totalItems]
  );

  const initialState = useMemo(
    () => ({ pageIndex: currentPage - 1 }),
    [currentPage]
  );

  const [uploadVideo] = useUploadVideoMutation();

  const {
    data: AdAccountData,
    isLoading: isAdAccountLoading,
    isFetching: isAdAccountFetching,
  } = useGetBmAdAccountsQuery(
    {
      search: searchTerm ? searchTerm : '',
      pageSize: '20',
      after: searchTerm ? '' : afterValue,
    },
    { skip: !isOpen }
  );

  // ---------- helpers ----------
  const normalizeRendered = (raw, index) => {
    const id = String(raw?._id ?? raw?.id ?? raw?.media_job ?? index);
    const name = raw?.name || raw?.title || `video_${id}`;
    const videoUrl = raw?.videoUrl || raw?.video_output || '';
    return { id, name, videoUrl, raw };
  };

  // Use stable list while fetching to prevent loader flicker
  const baseList = isRendFetching && stableVideos.length ? stableVideos : renderedVideos;

  // ---------- derived: hide pending ----------
  const selectableVideos = useMemo(() => {
    const list = Array.isArray(baseList) ? baseList : [];
    return list.filter((v) => v?.status !== 'pending');
  }, [baseList]);

  // drop selections that disappear
  useEffect(() => {
    if (!selectedRenderVideos.length) return;
    const selectableIds = new Set(selectableVideos.map((v, idx) => normalizeRendered(v, idx).id));
    setSelectedRenderVideos((prev) => prev.filter((s) => selectableIds.has(s.id)));
  }, [selectableVideos, selectedRenderVideos.length]);

  // ---------- formik ----------
  const uploadFormik = useFormik({
    initialValues: { adsAccountName: [], files: [] },
    validationSchema: adUplaodSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (values) {
        await handleUploadAll(values.files);
        dispatch(setIsWidgetOpen(true));
        dispatch(setFiles([]));
        dispatch(setAfter(''));
        dispatch(setBefore(''));
        localStorage.removeItem('after');
        localStorage.removeItem('before');
        onClose();
      }
      setSubmitting(false);
      resetForm();
      handleModalClose();
    },
  });

  const handleUploadAll = async (filesToUpload) => {
    window.addEventListener('online', () => {
      toast({ title: "You are back online!", status: "success", duration: 4000, isClosable: true, position: "top-right" });
    });
    window.addEventListener('offline', () => {
      toast({ title: "You are offline. Some functionality may be unavailable.", status: "error", duration: 4000, isClosable: true, position: "top-right" });
    });

    const uploadPromises = [];

    for (const file of filesToUpload) {
      const adsAccountsArray = uploadFormik?.values?.adsAccountName.map(accountId => {
        const selectedAdAccount = selectedAdAccounts?.find(account => account?.account_id === accountId);
        return selectedAdAccount ? {
          id: selectedAdAccount?._id,
          adsAccountId: selectedAdAccount?.account_id,
          adsAccountName: selectedAdAccount?.name,
          businessName: selectedAdAccount?.business?.name,
          businessId: selectedAdAccount?.business?.id,
        } : null;
      }).filter(Boolean);

      if (!adsAccountsArray.length) {
        toast({ title: "Account Error", description: "Please select a valid Ads Account.", status: "error", duration: 5000, isClosable: true, position: "top-right" });
        return;
      }

      if (file.type.startsWith("video/")) {
        const perFilePromises = adsAccountsArray.map(selectedAdAccount => (async () => {
          const abortController = new AbortController();
          const controllerKey = `${file.name}-${selectedAdAccount.adsAccountId}`;
          dispatch(setUploadAbortControllers({ [controllerKey]: abortController }));

          try {
            dispatch(updateUploadStatus({ fileName: file.name, accountId: selectedAdAccount.adsAccountId, accountName: selectedAdAccount.adsAccountName, status: "uploading", progress: 10 }));

            const payload = {
              aiGenerated: true,
              url: file?.thumbnail,
              adsAccount: {
                adsAccountId: selectedAdAccount.adsAccountId,
                adsAccountName: selectedAdAccount.adsAccountName,
                businessName: selectedAdAccount.businessName,
                businessId: selectedAdAccount.businessId
              }
            };

            await uploadVideo({ payload });

            dispatch(updateUploadStatus({ fileName: file.name, accountId: selectedAdAccount.adsAccountId, accountName: selectedAdAccount.adsAccountName, status: "done", progress: 100 }));
          } catch (error) {
            const reason = error?.message || 'Upload failed';
            const isCancelled = abortController?.signal?.aborted;

            dispatch(updateUploadStatus({
              fileName: file.name,
              accountId: selectedAdAccount.adsAccountId,
              accountName: selectedAdAccount.adsAccountName,
              status: isCancelled ? "cancelled" : "failed",
              progress: 0,
              reason: isCancelled ? "Upload cancelled" : reason
            }));

            if (!isCancelled) {
              toast({ title: "Upload Error", description: reason, status: "error", duration: 5000, isClosable: true, position: "top-right" });
            }
          }
        })());

        uploadPromises.push(...perFilePromises);
      }
    }

    await Promise.allSettled(uploadPromises);
  };

  const handleModalClose = () => {
    uploadFormik.resetForm();
    dispatch(setFiles([]));
    onClose();
    localStorage.removeItem('selectedAdspressoAdsAccount');
    setSelectedAdAccounts([]);
    setSelectedRenderVideos([]);
    setAftervalue('');
  };

  const handleSearchChange = useCallback((value) => setSearchTerm(value), []);
  const handleLoadMore = (after) => setAftervalue(after);

  const handleAdaccountChange = (value) => {
    if (!value) return;
    const isAlreadySelected = selectedAdAccounts.some((a) => a?.account_id === value?.account_id);
    if (isAlreadySelected) return;

    const accountDetails = { ...value?.account };
    setSelectedAdAccounts((prev) => {
      const updated = [accountDetails, ...prev];
      uploadFormik.setFieldValue('adsAccountName', updated.map((a) => a.account_id));
      return updated;
    });
  };

  const handleRemoveAdAccount = (accountId) => {
    setSelectedAdAccounts((prev) => {
      const updated = prev.filter((a) => a.account_id !== accountId);
      uploadFormik.setFieldValue('adsAccountName', updated.map((a) => a.account_id));
      return updated;
    });
  };

  // checkbox -> transform selection -> Formik.files + Redux setFiles
  const handleRenderVideoChange = (inputRaw, idx) => {
    const v = normalizeRendered(inputRaw, idx);
    const already = selectedRenderVideos.some((s) => s.id === v.id);

    const updatedSelection = already
      ? selectedRenderVideos.filter((s) => s.id !== v.id)
      : [v, ...selectedRenderVideos];

    setSelectedRenderVideos(updatedSelection);

    const timestamp = Date.now();
    const transformedFiles = updatedSelection.map((item, i) => {
      const sizeInMB = Math.floor(Math.random() * (20 - 16 + 1)) + 16;
      const sizeInBytes = sizeInMB * 1024 * 1024;

      const baseName = item.name?.replace(/\s+/g, '_') || 'video';
      const fileName = `${baseName}_${timestamp}_${i + 1}.mp4`;

      const dummyData = new Uint8Array(1024 * 1024 * 16); // 16MB
      const fakeFile = new File([dummyData], fileName, { type: 'video/mp4' });

      return {
        file: fakeFile,
        name: fileName,
        size: sizeInBytes,
        type: 'video/mp4',
        thumbnail: item.videoUrl || URL.createObjectURL(fakeFile),
        progress: 0,
        status: 'pending',
      };
    });

    dispatch(setFiles(transformedFiles));
    uploadFormik.setFieldValue('files', transformedFiles);
  };

  const cardWidth = { base: '100px', md: '140px' };
  const cols = { base: 2, sm: 3, md: 5 };

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} size="7xl" scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent h="90vh" w={{ base: '90vw', md: '88vw' }}>
        <ModalHeader>
          <ModalCloseButton />
          <Flex gap={2} direction="column">
            <Flex align="center" justify="space-between">
              <Text>Upload Videos</Text>
            </Flex>
          </Flex>
        </ModalHeader>
        <Divider mb="10px" />
        {/* Body gets the remaining height; inner panes scroll independently */}
        <ModalBody overflow="hidden">
          <Flex direction={{ base: 'column', md: 'row' }} h="100%" gap={4}>
            {/* Left: Ad Accounts */}
            <Box flex={{ base: '100%', md: '38%' }} h={{ base: 'auto', md: '100%' }} minH="320px" overflow="hidden">
              <Text mb={2} fontWeight="bold">
                Choose Ad Accounts
              </Text>
              <Text fontSize="12px" mb={2}>
                Select your Ad Accounts to upload multiple videos directly to their My Drive for seamless access during Ad Campaign creation.
              </Text>

              <SearchAdaccountAdspresso
                onInitialisation={() => {}}
                accountOptions={AdAccountData?.data}
                onAccountChange={handleAdaccountChange}
                onSearchInputChange={handleSearchChange}
                onSearchInputTouched={setHasSearched}
                AdsAccountIdFromParams={''}
                isAdAccountLoading={isAdAccountLoading}
                isAdAccountFetching={isAdAccountFetching}
                pageMeta={AdAccountData?.meta}
                onLoadMore={handleLoadMore}
                selectedAccounts={selectedAdAccounts}
                onRemoveAccount={handleRemoveAdAccount}
                isForStatusUpload={true}
              />

              <Box overflowY="auto" maxH="calc(100% - 160px)" pr={2} mt={2}>
                <Stack spacing={1} mt={2}>
                  {selectedAdAccounts?.map((account, index) => (
                    <Flex key={`${account?.account_id}-${index}`} align="center" direction="row" gap={1}>
                      <Flex direction="column" align="flex-start" flex={0.9} pl={1}>
                        <Text fontSize="12px">
                          {account?.name?.length > 38 ? `${account.name.substring(0, 38)}...` : account?.name}
                        </Text>
                        <Flex gap="5px">
                          <Text fontSize="11px" color="gray.500">
                            {account?.account_id}
                          </Text>
                        </Flex>
                      </Flex>
                      <Flex justify="flex-end" flex={0.1}>
                        <Tooltip label="Remove" aria-label="Remove account tooltip" fontSize="12px">
                          <IconButton
                            icon={<CloseIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleRemoveAdAccount(account.account_id)}
                            aria-label="Remove account"
                          />
                        </Tooltip>
                      </Flex>
                    </Flex>
                  ))}
                </Stack>
              </Box>
            </Box>

            {/* Right: Rendered videos with pagination pinned at bottom */}
            <Flex
              ref={rightPaneRef}
              direction="column"
              flex={{ base: '100%', md: '62%' }}
              h={{ base: 'auto', md: '100%' }}
              minH="420px"
              borderWidth="1px"
              borderRadius="md"
              p={3}
              position="relative"
              overflow="hidden"
            >
              <Flex justify="space-between" align="center" mb={2} shrink={0}>
                <Text fontWeight="bold">Select Videos</Text>
                <Text fontSize="sm" color="gray.500">{selectedRenderVideos.length} selected</Text>
              </Flex>

              {/* Grid area scrolls, pagination stays pinned at bottom */}
              <Box flex="1" overflow="auto" pr={1}>
                {isRendLoading && !stableVideos.length ? (
                  <Box w="full" minH="200px" display="grid" placeItems="center">
                    <Spinner />
                  </Box>
                ) : selectableVideos.length === 0 ? (
                  <Box w="full" minH="200px" display="grid" placeItems="center" textAlign="center" px={4}>
                    <Text fontSize="md" fontWeight="semibold">No generated videos</Text>
                    <Text fontSize="sm" color="gray.500">Once your renders are ready, they’ll show up here.</Text>
                  </Box>
                ) : (
                  <SimpleGrid columns={cols} spacing={3}>
                    {selectableVideos.map((raw, idx) => {
                      const v = normalizeRendered(raw, idx);
                      const checked = selectedRenderVideos.some((s) => s.id === v.id);

                      return (
                        <Box
                          key={v.id}
                          position="relative"
                          borderWidth="1px"
                          borderRadius="md"
                          overflow="hidden"
                          _hover={{ boxShadow: 'md' }}
                          w={cardWidth}
                          // ensure 10 visible on md+: 5 cols x 2 rows at ~140x
                        >
                          <AspectRatio ratio={2 / 3} w="full">
                            {v.videoUrl ? (
                              <Box
                                as="video"
                                src={v.videoUrl}
                                muted
                                playsInline
                                w="100%"
                                h="100%"
                                objectFit="cover"
                                borderRadius="3px"
                              />
                            ) : (
                              <Box bg="gray.100" />
                            )}
                          </AspectRatio>

                          <Checkbox
                            isChecked={checked}
                            colorScheme="brand"
                            onChange={() => handleRenderVideoChange(raw, idx)}
                            position="absolute"
                            top="6px"
                            left="6px"
                            bg="white"
                            border="1px solid"
                            borderRadius="4px"
                            transform="scale(0.9)"
                            transformOrigin="top left"
                          />
                        </Box>
                      );
                    })}
                  </SimpleGrid>
                )}
              </Box>

              {/* Always render pagination, even if < 10 items */}
              <Box mt={3} shrink={0}>
                <Pagination
                  PageMeta={pageMetaForComponent}
                  initialState={initialState}
                  isFetching={isRendFetching}
                  onPageChange={({ currentPage: nextIndex0 }) => {
                    const maxIndex0 = Math.max(0, Math.ceil(totalItems / pageSize) - 1);
                    const clamped = Math.min(Math.max(nextIndex0, 0), maxIndex0);
                    setCurrentPage(clamped + 1);
                  }}
                />
              </Box>
            </Flex>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Flex justifyContent="flex-end" width="100%" gap="20px">
            <Flex gap={4}>
              <Button colorScheme="gray" variant="solid" size="sm" onClick={handleModalClose}>
                Cancel
              </Button>
              <Button
                colorScheme="brand"
                size="sm"
                onClick={() => {
                  uploadFormik.handleSubmit();
                  dispatch(setIsWidgetOpen(true));
                  onClose();
                }}
                isDisabled={!uploadFormik.values.adsAccountName.length || !uploadFormik.isValid}
              >
                Submit
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
